"use client";

import { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { SplitChip, type SplitChipSegment } from "@/components/Inputs/base/SplitChip";
import { getGroupValue, setGroupValue } from "@/constants/features";
import { cn } from "@/lib/utils";
export type ToggleChipOption = {
  value: string;
  label: ReactNode;
  icon?: LucideIcon;
};

/** A mutually-exclusive tier (e.g. Wi-Fi | Fibra) stored inside the same value array. */
export type SplitChipGroup = {
  segments: SplitChipSegment[];
  groupKeys: string[];
};
type Props = {
  options: ToggleChipOption[];
  value: string[];
  onValueChange: (values: string[]) => void;
  disabled?: boolean;
  className?: string;
  /** Renders a single mutually-exclusive SplitChip inline after the chips. */
  splitGroup?: SplitChipGroup;
};

/**
 * Bare multi-select chips (icon + label). Controlled and unopinionated about
 * forms — wrap it (e.g. `FormToggleGroup`, `EditableFormChips`) to bind it to
 * `react-hook-form`.
 */
export function ToggleChips({
  options,
  value,
  onValueChange,
  disabled,
  className,
  splitGroup
}: Props) {
  return <ToggleGroup type="multiple" variant="outlined" value={value} onValueChange={onValueChange} disabled={disabled} className={cn("flex flex-wrap gap-3 justify-start", className)} data-sentry-element="ToggleGroup" data-sentry-component="ToggleChips" data-sentry-source-file="ToggleChips.tsx">
      {options.map(({
      value: v,
      label: l,
      icon: Icon
    }) => <ToggleGroupItem key={v} value={v} className="whitespace-nowrap rounded-full cursor-pointer gap-1.5">
          {Icon && <Icon className="size-4" />}
          {l}
        </ToggleGroupItem>)}
      {splitGroup && <SplitChip segments={splitGroup.segments} value={getGroupValue(value, splitGroup.groupKeys)} onValueChange={next => onValueChange(setGroupValue(value, splitGroup.groupKeys, next))} disabled={disabled} />}
    </ToggleGroup>;
}